import { create } from 'zustand'
// Conditional import for SSR compatibility - using dynamic import for ES modules
import * as FingerprintOpen from '@fingerprintjs/fingerprintjs'
import { start as startFingerprintPro, type GetResult } from '@fingerprint/agent'

/** Matches @fingerprint/react v3 provider tagging so dashboard analytics stay consistent with SDK 3.x. */
const FINGERPRINT_AGENT_INTEGRATION_INFO = [
  'react-sdk/3.0.0',
  'ff-admin/zustand-client',
] as const
import { Category } from '@/client/types/entity-full'
import { Device } from '@/client/types/product'
import { useUserStore } from '@stores/user'
import { PromotionSpecs } from '@/client/types/trade'
import { APIResponse } from '@/client/types/api'
import {
  generateIdempotencyKey,
  generateNonce,
  requiresSigning,
  signRequest,
  shouldUseIdempotency,
} from '@/client/utils/requestSecurity'
import { useApiHealthStore } from '@/client/stores/apiHealth'
import { useDataScopeStore } from '@/client/stores/data-scope'

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

interface DefaultHeaders {
  'x-client-tz'?: string
  'x-client-fpo'?: string
  'x-client-fpp'?: string
  'x-client-lcl'?: string
  'x-client-loc'?: string
  'Content-Type'?: string
  authanon?: string
  sidanon?: string
}

interface GeoLocation {
  ip: string;
  continent_code: string;
  continent_name: string;
  country_code2: string;
  country_code3: string;
  country_name: string;
  country_name_official: string;
  country_capital: string;
  state_prov: string;
  state_code: string;
  district: string;
  city: string;
  zipcode: string;
  latitude: string;
  longitude: string;
  is_eu: boolean;
  country_flag: string;
  geoname_id: string;
  country_emoji: string;
  calling_code: string;
  country_tld: string;
  languages: string;
  isp: string;
  connection_type: string;
  organization: string;
  currency: {
    code: string;
    name: string;
    symbol: string;
  };
  time_zone: {
    name: string;
    offset: number;
    offset_with_dst: number;
    current_time: string;
    current_time_unix: number;
    is_dst: boolean;
    dst_savings: number;
    dst_exists: boolean;
    dst_start: string;
    dst_end: string;
  };
}

interface ClientInfo {
  isInitializingClient: boolean
  defaultHeaders: DefaultHeaders | null
  fingerprint: string
  timezone: string
  authanon?: string
  sidanon?: string
  geolocation: GeoLocation | null
  categories: Category[] | null
  promotionSpecs: PromotionSpecs | null
  mdevices: Device[] | null
  initialized: boolean
}

interface FPSOpenInterface {
  authanon?: string
  fp: FingerprintOpen.GetResult
  open_visitorId?: string
}

interface FPSProInterface {
  authanon?: string
  fp: GetResult
  open_visitorId?: string
}

interface FPSDatapoint {
  fpoid?: string
  fppid?: string
  geolocate: boolean
  payload(): any
}

interface ClientStore extends ClientInfo {
  logfps: ( datapoint: FPSDatapoint ) => void
  request: <T = unknown>(
    endpoint: string,
    method?: HttpMethod,
    body?: unknown,
    config?: { timeout?: number; idempotencyKey?: string }
  ) => Promise<T>
  store: Storage
  geolocate: (openVisitorId?: string) => Promise<void>
  /** Force a location refresh (e.g. "Update my location" in settings). Resets one-per-session guard. */
  refreshGeolocation: () => Promise<void>

  setLocRegion: (countrycode: string, region: string) => void
  setLanguage: (lang: string) => void

  fetchCategories: () => Promise<void>
  fetchMobileDevices: () => Promise<void>
  fetchPromotionSpecs: (options?: { etid?: number, for_user?: number, for_org?: number, for_ext_link?: number }) => Promise<void>

  initialize: ( callback?: () => void ) => Promise<void>
}

// Session verification state — prevents a single background 401 from
// immediately logging the user out. Instead we confirm via /users/info
// (which the server always returns as HTTP 200) before clearing.
let _sessionVerifyInFlight: Promise<void> | null = null
let _lastSessionVerifyAt = 0
const _SESSION_VERIFY_COOLDOWN = 10_000 // 10 seconds between verifications

// Consecutive-401 tracking. A single 401 may be transient (confirmed via the
// /users/info re-check below), but a burst of them within a short window means
// the bearer token is genuinely dead — even when /users/info manages to
// self-heal (AES-key/token refresh) and reports the session as still valid.
// Once we cross the threshold we force logout regardless of that re-check.
// The counter is reset by any successful response (see recordSuccess path).
let _consecutive401s = 0
let _auth401WindowStart = 0
const _AUTH_401_WINDOW = 15_000 // window over which 401s are counted
const _AUTH_401_THRESHOLD = 3 // this many 401s within the window → force logout

// Route users to sign-in after an auth failure. Clears the persisted user
// store, then hard-navigates to `/` (the sign-in route) — a full reload wipes
// in-memory Zustand state and the React Query cache so no stale authed data
// lingers. Guarded against redirect loops (skip if already on the auth page)
// and against firing more than once per expiry.
let _loggingOut = false
const forceLogout = (reason: string) => {
  if (_loggingOut) return
  _loggingOut = true
  try {
    console.warn(`[CLIENT] Logging out — ${reason}`)
    useUserStore.getState().clearUser()
  } catch (e) {
    console.error('[CLIENT] clearUser during forced logout failed:', e)
  }
  if (typeof window !== 'undefined' && window.location.pathname !== '/') {
    window.location.assign('/')
  } else {
    // Already on the sign-in route — allow future logouts to proceed.
    _loggingOut = false
  }
}

// Initialize storage lazily - will be set after module loads
let store: any = null;
if (typeof window !== 'undefined') {
  import('all-localstorage').then((mod) => {
    const Storage = mod.default || mod;
    if (Storage) {
      store = new Storage({
        prefix: 'engts',
        encrypt: true,
        token: 'pi32hqb5weNtJO6w6qQkBDqZhQa4'
      });
    }
  }).catch(() => {
    store = null;
  });
}

const LAST_GEO_OPEN_VISITOR_KEY = 'lastGeolocatedOpenVisitorId'
/** Persisted "did we spend a Pro identification on this browser?" verdict. */
const FPP_DECISION_KEY = 'fppDecision'

/** One recorded Pro decision, scoped to the open fingerprint it was made under. */
interface FppDecision {
  open_visitorId: string
  /** true = this browser is allowed to spend a Fingerprint Pro identification. */
  ran: boolean
  /** Rate the verdict was drawn at, so raising the rate can re-open a "no". */
  pct: number
}

/**
 * Parse a 0-100 rate from env.
 *
 * `Number(raw) || fallback` cannot express zero — `Number('0') || 25` is 25 —
 * which is why the old single knob could be turned up but never off.
 */
function readRatePercent( raw: string | undefined, fallback: number ): number {
  if( raw == null || raw === '' ) return fallback
  const n = Number(raw)
  if( !Number.isFinite(n) ) return fallback
  return Math.min(100, Math.max(0, n))
}

/** Open agent: free, runs in-browser, no metered call. */
function openSampleRatePercent(): number {
  return readRatePercent( import.meta.env.VITE_PUBLIC_FINGERPRINT_SAMPLE_RATE, 100 )
}

/** Pro agent: billed per identification. Off by default on the admin surface. */
function proSampleRatePercent(): number {
  return readRatePercent( import.meta.env.VITE_PUBLIC_FINGERPRINT_PRO_SAMPLE_RATE, 0 )
}

/**
 * Decide ONCE per open fingerprint whether this browser may spend a Pro call.
 *
 * Sampling on every load re-rolls the dice, so a browser that lost the roll
 * today gets billed tomorrow and the rate describes loads rather than browsers.
 * Persisting the verdict makes it one decision per browser identity.
 */
function proAllowedForOpenVisitor( openVisitorId: string ): boolean {
  if( !openVisitorId ) return false
  const pct = proSampleRatePercent()

  try {
    const cached = store?.get(FPP_DECISION_KEY) as FppDecision | undefined
    if( cached
        && cached.open_visitorId === openVisitorId
        && ( cached.ran || (cached.pct ?? 0) >= pct ) )
      return cached.ran
  }
  catch( error ){ /* storage unavailable — decide fresh */ }

  const ran = pct > 0 && Math.random() * 100 < pct
  try { store?.set(FPP_DECISION_KEY, { open_visitorId: openVisitorId, ran, pct }) }
  catch( error ){ /* verdict simply is not sticky this load */ }
  return ran
}

let
GEOLOCATED = false,
GEOLOCATING = false,
/** Open fingerprint visitor id we last ran geolocate for; when it changes we refresh location. Synced from store on first use. */
lastGeolocatedOpenVisitorId: string | null = null,

FPS_LOGGED = false,
FPS_PRO_LOGGING = false,
FPS_OPEN_LOGGING = false,
FPS_INITIALIZING = false,
FPS_INITIALIZED: FPSDatapoint

async function getFingerPrints(){
  let
  openVisitorId = '',
  fpopayload: FPSOpenInterface,
  fpppayload: FPSProInterface | undefined,
  geolocate: boolean

  if( FPS_INITIALIZED )
    return FPS_INITIALIZED

  FPS_INITIALIZING = true
  try {
    /**
     * Open Fingerprints: Always query this
     * data at every session start.
     */
    const fpo = await FingerprintOpen.load()
    
    fpopayload = {
      fp: await fpo.get()
    }

    openVisitorId = fpopayload.fp.visitorId
    FPS_INITIALIZED = {
      geolocate: false,
      fpoid: fpopayload.fp.visitorId,
      payload(){ return { fpopayload, fpppayload: undefined, } }
    }
  }
  catch( error ){
    console.log('Open fingerprint gen error --', error )
  }

  try {
    fpppayload = store?.get('fpppayload') || undefined

    // Location refresh is keyed to the OPEN visitor id, never to the presence of
    // a Pro payload. Deriving it from `!fpppayload` meant that turning Pro down
    // left this permanently true and moved the spend onto ipgeolocation instead.
    const lastGeoId = lastGeolocatedOpenVisitorId
                      ?? (store?.get(LAST_GEO_OPEN_VISITOR_KEY) as string | null)
                      ?? null
    geolocate = !!openVisitorId && lastGeoId !== openVisitorId

    const proStale = !fpppayload || fpppayload.open_visitorId !== openVisitorId
    // A payload minted under a different browser identity must not be reported
    // as this browser's, whether or not we mint a replacement.
    if( proStale ) fpppayload = undefined

    if( proStale && proAllowedForOpenVisitor(openVisitorId) ){
      const subdomain = import.meta.env.VITE_PUBLIC_FINGERPRINT_PRO_SUBDOMAIN
      const fpp = startFingerprintPro({
        apiKey: import.meta.env.VITE_PUBLIC_FINGERPRINT_PRO_APIKEY || '',
        endpoints: subdomain ? [subdomain] : undefined,
        region: 'eu',
        integrationInfo: [...FINGERPRINT_AGENT_INTEGRATION_INFO],
      })

      fpppayload = {
        open_visitorId: openVisitorId,
        fp: await fpp.get()
      }

      store?.set('fpppayload', fpppayload )
    }

    // visitor_id is optional in agent v4+ (e.g. zero-trust); fall back for headers/logging.
    const proVisitorId = fpppayload?.fp?.visitor_id ?? ''

    FPS_INITIALIZED = {
      ...FPS_INITIALIZED,
      fppid: proVisitorId || undefined,
      geolocate,
      payload(){ return { fpopayload, fpppayload } }
    }
  }
  catch( error ){
    console.log('Fingerprint pro gen error --', error )
  }

  FPS_INITIALIZING = false
  return FPS_INITIALIZED
}

export const useClientStore = create<ClientStore>((set, get) => ({
  isInitializingClient: false,
  fingerprint: '',
  timezone: '',
  authanon: undefined,
  sidanon: undefined,
  regions: null,
  mdevices: null,
  categories: null,
  promotionSpecs: null,
  defaultHeaders: null,
  geolocation: null,
  initialized: false,
  store,

  // Enhanced request handling with timeout support
  request: async (endpoint, method = 'GET', body?, options?: { timeout?: number; idempotencyKey?: string }) => {
    const state = get()
  
    console.log("REQUEST:::", endpoint, method, body, options)
    // Upload requests are now handled by Next.js rewrites configuration
    let requestUrl = endpoint;
  
    const requestOptions: any = {
      method,
      credentials: 'include',
      cache: 'no-store' as RequestCache,
    }
    
    const headers: any = {
      ...state.defaultHeaders,
      // Extra-headers
      'x-client-lcl': state.geolocation && state.geolocation.languages.split(',')[0] || navigator.language,
      // country_code2, state_prov, district, city
      'x-client-loc': state.geolocation ? `${state.geolocation.country_code2}, ${state.geolocation.state_prov}, ${state.geolocation.district}, ${state.geolocation.city}` : 'GH,,,',
      // Admin-wide live/test switch (stores/data-scope); ff-api list/count endpoints read it.
      'x-admin-data-scope': useDataScopeStore.getState().scope,
    }

    // SSR / RSC requests run on the Next.js server where fetch() doesn't
    // automatically include a User-Agent. The API rejects requests without one.
    // Browser requests already carry the browser UA so this is a no-op there.
    if (typeof window === 'undefined') {
      headers['User-Agent'] = 'ff-web/1.0 (+nextjs)';
    }

    // Add Idempotency-Key header for POST/PUT/PATCH/DELETE requests (optional but recommended)
    if (shouldUseIdempotency(method)) {
      headers['Idempotency-Key'] = options?.idempotencyKey || generateIdempotencyKey();
    }
    
    // Per-user request signing for sensitive endpoints
    let bodyString: string | null = null;
    const needsSigning = requiresSigning(method, endpoint);
    
    if (needsSigning) {
      const user = useUserStore.getState().user;
      const signingSecret = user?.signing_secret ?? null;
      const authid = user?.authid ?? null;
      
      if (body instanceof FormData) {
        bodyString = '';
      } else if (body) {
        bodyString = JSON.stringify(body);
      }
      
      if (!signingSecret || !authid) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            'Request signing required but user signing_secret or authid missing:',
            endpoint,
            { hasSecret: !!signingSecret, hasAuthid: !!authid }
          );
        } else {
          throw new Error(
            `Request signing required for ${method} ${endpoint}. Sign in again or regenerate signing secret.`
          );
        }
      } else {
        const timestamp = Math.floor(Date.now() / 1000);
        const nonce = generateNonce();
        try {
          const { signature, timestamp: ts, nonce: n } = await signRequest(
            method,
            endpoint,
            timestamp,
            nonce,
            bodyString,
            signingSecret
          );
          headers['authid'] = authid;
          headers['X-Request-Signature'] = signature;
          headers['X-Signature-Timestamp'] = ts;
          headers['X-Request-Nonce'] = n;
        } catch (err) {
          console.error('Failed to sign request:', err);
          if (process.env.NODE_ENV === 'production') {
            throw new Error(`Failed to sign request: ${err}`);
          }
        }
      }
    }

    // User Notifications API requires authid on all endpoints (with or without signing)
    const pathWithoutQuery = endpoint.split('?')[0];
    if (pathWithoutQuery.startsWith('/api/v2/user/notification')) {
      const user = useUserStore.getState().user;
      const authid = user?.authid ?? null;
      if (authid) headers['authid'] = authid;
    }
  
    // Set up timeout with AbortController for browser-side timeout
    const controller = new AbortController();
    requestOptions.signal = controller.signal;
    
    let timeoutId: NodeJS.Timeout | undefined;
    if (options?.timeout) {
      // Send timeout to proxy server (for backend request timeout)
      headers['x-client-timeout'] = options.timeout;
      
      // Set browser-side timeout (for browser -> proxy timeout)
      timeoutId = setTimeout(() => controller.abort(), options.timeout);
    }
  
    if (body instanceof FormData) {
      const { 'Content-Type': _, ...customHeaders } = headers;
      console.log("INSTANCE OF REQUEST BODY:::", body instanceof FormData)
  
      requestOptions.headers = customHeaders;
      requestOptions.body = body;
    } else {
      requestOptions.headers = headers
      if (body) {
        requestOptions.body = JSON.stringify(body);
      }
    }
  
    try {
      const response = await fetch(requestUrl, requestOptions);
      
      // Check response status and handle errors gracefully
      if (!response.ok) {
        // Try to parse error details from JSON response
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorData: any = null;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
            // Some endpoints double-nest under data.data ({ status, data: { data: { error_description } } });
            // check that before the single-nested and top-level shapes.
            if (errorData?.data?.data?.error_description) {
              errorMessage = errorData.data.data.error_description;
            } else if (typeof errorData?.data?.data?.error === 'string' && errorData.data.data.error !== 'invalid_credentials') {
              errorMessage = errorData.data.data.error;
            } else if (errorData?.data?.error_description) {
              errorMessage = errorData.data.error_description;
            } else if (errorData?.data?.error) {
              // If error is a string like "invalid_credentials", provide user-friendly message
              if (errorData.data.error === 'invalid_credentials') {
                errorMessage = 'Invalid email, username, or password. Please check your credentials and try again.';
              } else if (typeof errorData.data.error === 'string') {
                errorMessage = errorData.data.error;
              }
            } else if (errorData?.data?.message) {
              errorMessage = errorData.data.message;
            }
            // Check top-level fields (fallback for different response formats)
            else if (errorData?.error_description) {
              errorMessage = errorData.error_description;
            } else if (errorData?.message) {
              errorMessage = errorData.message;
            } else if (errorData?.error) {
              if (typeof errorData.error === 'string') {
                errorMessage = errorData.error;
              } else if (errorData.error?.error_description) {
                errorMessage = errorData.error.error_description;
              } else if (errorData.error?.message) {
                errorMessage = errorData.error.message;
              }
            }
          }
        } catch {
          // If JSON parsing fails, use default error message
          // This handles HTML error pages, plain text, etc.
        }

        // 404 on public engagement count = no data yet; treat as success with empty array (no console noise)
        if (response.status === 404 && requestUrl.includes('/public/engagement/count/')) {
          return [] as any;
        }

        // 408 on messaging: upstream slow; MessagingProvider handles gracefully — one short log
        const isMessagingTimeout = response.status === 408 && (requestUrl.includes('/messaging/') || requestUrl.includes('/utils/messaging/'));
        // Transient server errors on background data fetches: suppress console noise
        const isTransientServerError =
          [408, 502, 503, 504].includes(response.status) &&
          (requestUrl.includes('/notifications/unread-count') ||
           requestUrl.includes('/engagement/bulk/status/') ||
           requestUrl.startsWith('/public/'));
        // Expected eligibility/validation errors — caller handles these, no need to warn
        const isExpectedValidationError =
          response.status === 400 &&
          (requestUrl.includes('/email/eligibility') ||
           requestUrl.includes('/phone/eligibility') ||
           requestUrl.includes('/account/email/') ||
           requestUrl.includes('/account/phone/'));
        if (!isMessagingTimeout && !isTransientServerError && !isExpectedValidationError) {
          console.warn(`[API ${response.status}] ${requestUrl}:`, errorMessage);
        }
        const error: any = new Error(`${errorMessage}`);
        error.status = response.status;
        error.statusCode = response.status;
        error.data = errorData;
        error.url = requestUrl;
        if (response.status >= 500) {
          useApiHealthStore.getState().recordFailure();
        }
        throw error;
      }
      
      // A successful response means the bearer token is working again —
      // clear the consecutive-401 streak so a later isolated 401 starts fresh.
      _consecutive401s = 0

      // Only parse JSON if response was successful
      // Guard against empty or non-JSON response bodies (e.g., drained redirects,
      // truncated streams, or 204 No Content responses that slip through)
      const contentType = response.headers.get('content-type');
      const text = await response.text();
      
      if (!text || text.trim().length === 0) {
        // Empty body — return a minimal success envelope so callers don't crash
        return { status: response.status, data: null } as any;
      }

      useApiHealthStore.getState().recordSuccess();

      // Only attempt JSON parse if body looks like JSON
      if (contentType?.includes('application/json') || text.trimStart().startsWith('{') || text.trimStart().startsWith('[')) {
        try {
          return JSON.parse(text);
        } catch {
          console.warn(`[API] Failed to parse JSON from ${requestUrl} (${text.length} chars)`);
          return { status: response.status, data: text } as any;
        }
      }
      
      // Non-JSON success response — return as-is
      return { status: response.status, data: text } as any;
    } catch (error: any) {
      // Clean up timeout first
      if (timeoutId) clearTimeout(timeoutId);
      
      // Check if error was due to abort (timeout)
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        const timeoutError: any = new Error(`Request timeout after ${options?.timeout || 30000}ms`);
        timeoutError.status = 408; // Request Timeout
        timeoutError.statusCode = 408;
        timeoutError.code = 'TIMEOUT';
        timeoutError.url = requestUrl;
        // Don't log timeout errors as errors - they're expected for slow APIs
        console.warn('Request timeout:', requestUrl, 'Method:', method, `(${options?.timeout || 30000}ms)`);
        throw timeoutError;
      }
      
      // Check if it's a messaging endpoint - handle auth/server errors gracefully
      const isMessagingEndpoint = 
        requestUrl.includes('/messaging/') ||
        requestUrl.includes('/utils/messaging/');
      
      // For 401/403 on messaging endpoints, suppress error logging (handled gracefully upstream)
      if (isMessagingEndpoint && (error.status === 401 || error.status === 403)) {
        // Don't log as error - this is expected for non-authenticated users
        console.debug('[Messaging] Auth error on messaging endpoint (suppressed):', requestUrl);
        throw error; // Still throw, but don't log as error
      }
      
      // For 500 on messaging endpoints, log as warning instead of error
      if (isMessagingEndpoint && error.status >= 500) {
        console.warn('[Messaging] Server error on messaging endpoint:', requestUrl, error.status);
        throw error; // Still throw, but log as warning
      }
      
      // Log non-timeout errors (but not as critical errors for non-essential endpoints)
      const isNonEssentialEndpoint = 
        requestUrl.includes('/public/tag/popular') ||
        requestUrl.includes('/public/menu/ids') ||
        requestUrl.includes('/public/user/fp/') ||
        requestUrl.includes('/public/currency/');
      
      if (isNonEssentialEndpoint) {
        console.warn('Non-essential API request failed:', requestUrl, error.message || error);
      } else {
        console.error('Request failed:', error, 'URL:', requestUrl, 'Method:', method);
      }
  
      if (useUserStore.getState().user && error.status === 401) {
        const now = Date.now()

        // Count 401s within a rolling window. The window resets if enough time
        // has passed since the first 401 of the current streak.
        if (now - _auth401WindowStart > _AUTH_401_WINDOW) {
          _auth401WindowStart = now
          _consecutive401s = 0
        }
        _consecutive401s += 1

        // Genuinely dead token: too many 401s in the window. Log out now, even
        // if /users/info would have self-healed and called this "transient".
        // This runs outside the verification cooldown so a burst of 401s can't
        // be masked by a single earlier "session still valid" verdict.
        if (_consecutive401s >= _AUTH_401_THRESHOLD) {
          forceLogout(`${_consecutive401s} consecutive 401s (last on ${requestUrl})`)
        } else if (!_sessionVerifyInFlight && now - _lastSessionVerifyAt > _SESSION_VERIFY_COOLDOWN) {
          _lastSessionVerifyAt = now
          console.warn('[CLIENT] 401 received — verifying session before clearing…')

          // _sessionVerifyInFlight = fetch('/api/v2/users/info', {
          _sessionVerifyInFlight = fetch('/api/admin/users/info', {
            credentials: 'include',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json' },
          })
            .then(async res => {
              // A 5xx (or DigitalOcean's HTML 504 page during a deploy) says
              // nothing about the session: the check could not run. Only an
              // answer that parses and says 401 / no user means logged out.
              if (res.status >= 500) throw new Error(`users/info answered ${res.status}`)
              return res.json()
            })
            .then(data => {
              if (data.status === 401 || !data.data) {
                // Session is genuinely gone → clear the store AND redirect to
                // sign-in, so an expired token can't leave the user sitting on
                // an authed screen with a half-cleared store.
                forceLogout(`session expired (401 on ${requestUrl})`)
              } else {
                console.log('[CLIENT] Session still valid — 401 was transient for', requestUrl)
              }
            })
            .catch((err) => {
              // Network error, timeout, deploy swap or a non-JSON page: keep the
              // user signed in. A dead session still logs out via the 401 streak
              // above or the next successful check.
              console.warn('[CLIENT] Session check could not run; staying signed in:', err?.message || err)
              _lastSessionVerifyAt = 0
            })
            .finally(() => {
              _sessionVerifyInFlight = null
            })
        }
      }
  
      // Ensure error has status property for consistent error handling
      if (!error.status && !error.statusCode) {
        error.status = error.statusCode = 500;
      }

      useApiHealthStore.getState().recordFailure();
      throw error;
    } finally {
      // Clean up timeout (in case it wasn't cleaned up in catch)
      if (timeoutId) clearTimeout(timeoutId);
    }
  },

  logfps: async datapoint => {
    if( FPS_LOGGED || FPS_PRO_LOGGING || FPS_OPEN_LOGGING ) return

    const
    { fpopayload, fpppayload, geolocate } = datapoint.payload(),
    { request } = get()

    if( fpppayload )
      try {
        FPS_PRO_LOGGING = true
        // Use shorter timeout for fingerprint logging (non-critical)
        const response = await request<APIResponse<any>>('/public/user/fp/pro', 'POST', fpppayload, { timeout: 10000 } )

        response.data.success
        && set(state => ({
          defaultHeaders: {
            ...state.defaultHeaders,
            authanon: response.data.authanon,
            sidanon: response.data.sidanon
          }
        }))
      }
      catch( error ){
        FPS_PRO_LOGGING = false // FIXED: Was setting to true on error
        // Silently fail - fingerprint logging is non-critical
        console.warn('Fingerprint pro logging failed (non-critical):', error)
      }
    
    if( fpopayload )
      try {
        FPS_OPEN_LOGGING = true
        // Use shorter timeout for fingerprint logging (non-critical)
        await request<APIResponse<any>>('/public/user/fp/open', 'POST', { ...fpopayload, fp_pro: fpppayload }, { timeout: 10000 } )
      }
      catch( error ){
        FPS_OPEN_LOGGING = false
        // Silently fail - fingerprint logging is non-critical
        console.warn('Fingerprint open logging failed (non-critical):', error)
      }
  
    // Location refresh triggers: no cached geo, new FP Pro session (geolocate), Pro/Open visitorId mismatch,
    // or open fingerprint changed (different device/browser). Last open visitor id persisted so we only
    // refresh when it actually changes across reloads.
    const currentOpenVisitorId = fpopayload?.fp?.visitorId ?? null
    const persistedLastId = get().store?.get(LAST_GEO_OPEN_VISITOR_KEY) ?? null
    const lastId = lastGeolocatedOpenVisitorId ?? persistedLastId
    if (persistedLastId != null && lastGeolocatedOpenVisitorId === null) lastGeolocatedOpenVisitorId = persistedLastId
    const openFingerprintChanged = currentOpenVisitorId != null && currentOpenVisitorId !== lastId
    const shouldGeolocate =
      !get().geolocation
      || geolocate
      || (fpppayload && fpppayload.open_visitorId !== fpopayload.fp.visitorId)
      || openFingerprintChanged

    if (shouldGeolocate) {
      if (openFingerprintChanged) GEOLOCATED = false
      get().geolocate(currentOpenVisitorId ?? undefined)
    } else if (get().geolocation && currentOpenVisitorId != null) {
      lastGeolocatedOpenVisitorId = currentOpenVisitorId
      get().store?.set(LAST_GEO_OPEN_VISITOR_KEY, currentOpenVisitorId)
    }

    FPS_LOGGED = true
  },

  /** Fetches IP-based location (ipgeolocation.io), stores in state + localStorage, POSTs to /public/user/geo. */
  geolocate: async (openVisitorId?: string) => {
    if (GEOLOCATED || GEOLOCATING) return

    const apiKey = import.meta.env.VITE_PUBLIC_GEOLOCATION_APIKEY || ''
    if (!apiKey) {
      console.log('Failed to geolocate user -- VITE_PUBLIC_GEOLOCATION_APIKEY is not set')
      return
    }

    GEOLOCATING = true
    try {
      const geoResponse = await fetch(`https://api.ipgeolocation.io/ipgeo?apiKey=${apiKey}`);
      if (!geoResponse.ok)
        throw new Error(`Geolocation API error: ${geoResponse.status}`);

      const geo: GeoLocation = await geoResponse.json();

      store?.set('geolocation', geo)
      set({ geolocation: geo })

      const authanon = get().defaultHeaders?.authanon
      if (authanon) {
        await get().request<APIResponse<any>>('/public/user/geo', 'POST', { geo, authanon })
      }
      GEOLOCATED = true
      if (openVisitorId != null) {
        lastGeolocatedOpenVisitorId = openVisitorId
        get().store?.set(LAST_GEO_OPEN_VISITOR_KEY, openVisitorId)
      }
    }
    catch (error) {
      console.log('Failed to geolocate user -- ', error)
    }
    finally { GEOLOCATING = false }
  },

  refreshGeolocation: async () => {
    GEOLOCATED = false
    await get().geolocate()
  },
  // Optional future: refresh when app returns to foreground if geo older than N hours; or long-interval periodic refresh (e.g. 24h).

  setLocRegion: ( countrycode, region ) => {
    set(state => ({ 
      defaultHeaders: {
        ...state.defaultHeaders,
        'x-client-loco': `${countrycode},${region},,`
      } }))
  },
  setLanguage: ( lang ) => {
    set(state => ({ defaultHeaders: { ...state.defaultHeaders, 'x-client-lclo': lang } }))
  },

  fetchCategories: async () => {
    try {
      const
      { request, store } = get()
      
      // Use cached list
      const categories = store?.get('categories')
      if( categories ){
        set({ categories })
        return categories
      }
      
      // Fetch fresh categories
      const response = await request<APIResponse<Category[]>>(`/public/entity/type/hierarchy`)
      if( response.status > 201 )
        throw new Error('Failed fetching categories')

      store?.set('categories', response.data )
      set({ categories: response.data })
    }
    catch( error ){ 
      console.log('Request error  --', error );
    }
  },

  fetchMobileDevices: async () => {
    try {
      const { request, store } = get()
      
      // Fetch mobile devices
      const response = await request<APIResponse<Device[]>>(`/api/v2/entity/mobile/device/catalog`)
      if( response.status > 201 )
        throw new Error('Failed fetching mobile devices')

      set({ mdevices: response.data })
    }
    catch( error ){ 
      console.log('Request error  --', error );
    }
  },

  fetchPromotionSpecs: async ( options ) => {
    try {
      const { request } = get()

      let endpoint = '/api/v2/promotion/specs'
      if( options ){
        if( options.etid ) endpoint += `?etid=${options.etid}`
        if( options.for_org ) endpoint += `?for_org=1`
        if( options.for_user ) endpoint += `?for_user=1`
        if( options.for_ext_link ) endpoint += `?for_ext_link=1`
      }
      
      const response = await request<APIResponse<PromotionSpecs>>( endpoint )
      if( response.status > 201 )
        throw new Error('Failed fetching promotion specs')

      set({ promotionSpecs: response.data })
    }
    catch( error ){
      console.log('Request error  --', error )
      throw error
    }
  },

  initialize: async ( callback?: () => void ) => {
    const state = get()

    // Already done — don't reset `initialized` to false. The callback (userStore.initialize)
    // has its own internal guard so calling it again is safe and instant.
    // This also prevents React StrictMode's double-effect invocation from causing a
    // brief `initializedClient: false` flash that shows RestrictedPage's loading screen.
    if (state.initialized && !state.isInitializingClient) {
      typeof callback === 'function' && callback()
      return
    }

    if (FPS_INITIALIZING) return

    set({ isInitializingClient: true, initialized: false })

    try {
      const
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
        initState: Partial<ClientStore> = {
        timezone,
        // CHANGED: Set to true synchronously so queries can start immediately
        initialized: true,
        geolocation: get().store?.get('geolocation') || null
      }
      
      // PERFORMANCE: Initialize with minimal data first, get fingerprints in background
      if( !state.defaultHeaders )
        initState.defaultHeaders = {
          'x-client-tz': timezone,
          'x-client-fpo': '', // Will be set asynchronously
          'x-client-fpp': '', // Will also be set asynchronously
          'x-client-lcl': '',
          'x-client-loc': '',
          'Content-Type': 'application/json',
        }

      // CHANGED: Set initialized:true BEFORE background tasks start
      // This allows queries to begin immediately
      set(initState)

      // PERFORMANCE: Move all heavy operations to background. Sample rate (e.g. 25%) run full fingerprint + logfps; rest run lightweight (geolocate if needed, categories).
      const initializeBackgroundTasks = async () => {
        // Governs the OPEN agent only — free, and what `x-client-fpo` carries.
        // The billed Pro agent has its own knob inside getFingerPrints().
        const runFingerprint = Math.random() * 100 < openSampleRatePercent()

        try {
          if (runFingerprint) {
            const fingerprints = await getFingerPrints()
            set(state => ({
              fingerprint: fingerprints.fpoid,
              defaultHeaders: {
                ...state.defaultHeaders,
                'x-client-fpo': fingerprints.fpoid || '',
                'x-client-fpp': fingerprints.fppid || ''
              }
            }))
            const { logfps, fetchCategories, fetchMobileDevices } = get()
            const isAuthed = !!useUserStore.getState().user
            await Promise.resolve(logfps(fingerprints))
            // const tasks: Array<Promise<any>> = [fetchCategories()]
            // if (isAuthed) tasks.push(fetchMobileDevices())
            // await Promise.all(tasks)
          } else {
            const { geolocate, fetchCategories, fetchMobileDevices } = get()
            const isAuthed = !!useUserStore.getState().user
            if (!get().geolocation) await geolocate()
            // const tasks: Array<Promise<any>> = [fetchCategories()]
            // if (isAuthed) tasks.push(fetchMobileDevices())
            // await Promise.all(tasks)
          }
        } catch (error) {
          console.warn('Background initialization tasks failed:', error);
        }
      }

      // Start background tasks but don't wait for them
      initializeBackgroundTasks()

      typeof callback === 'function' && callback()
    } catch (error) {
      console.error('Failed to initialize client info:', error)
      // IMPORTANT: Set initialized to true even on error so app doesn't hang
      set({ initialized: true })
      throw error
    }
    finally {
      set({ isInitializingClient: false })
    }
  }
}))